/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

/* mobile quickbuy */
 document.addEventListener('DOMContentLoaded', function() {
    const drawer = document.getElementById('quickBuyDrawer');
    const drawerContent = drawer.querySelector('.drawer-content');
    const closeButton = drawer.querySelector('.drawer-close');
    const loadingSpinner = `
      <div class="loading-spinner">
        <div class="spinner"></div>
      </div>`
    ;
    const backdrop = document.querySelector('.drawer-backdrop');
    
    // Function to open drawer
    function openDrawer() {
      drawer.classList.add('active');
      backdrop.classList.add('active');
    }

    // Function to close drawer
    function closeDrawer() {
      drawer.classList.remove('active');
      backdrop.classList.remove('active');
    }

    // Handle quickbuy toggle clicks
    document.addEventListener('click', async function(e) {
        const toggle = e.target instanceof Element
          ? e.target.closest('a.quickbuy-toggle-mobile')
          : null;
        if (!toggle || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        e.stopPropagation();
        
        // Show drawer immediately with loading spinner
        drawerContent.innerHTML = loadingSpinner;
drawer.dataset.cornerQuickbuy = String(Boolean(toggle.closest('#corner-cowi-related-items-card-items-wrapper')));
openDrawer();
        
        try {
          const card = toggle.closest('product-block');
const imageLink = card?.querySelector('.image-cont a.product-link');
const productUrl = imageLink?.href || toggle.href;
          // Fetch the product page content
          const response = await fetch(productUrl);
          
          const html = await response.text();
          
          const tmpl = document.createElement('template');
          tmpl.innerHTML = html;
          const newDetail = tmpl.content.querySelector('.quickbuy-content').cloneNode(true);
const genlookProduct = tmpl.content.querySelector('#genlook-page-product');
if (genlookProduct) newDetail.dataset.vpaGenlookProduct = genlookProduct.textContent;
newDetail.querySelectorAll('variant-picker').forEach((picker) => {
  picker.dataset.quickbuyUrl = productUrl;
});
          // convert to quickbuy content
          newDetail.querySelectorAll('.more').forEach((el) => { el.href = productUrl; });
          newDetail.querySelectorAll('.product-title').forEach((el) => {
            el.innerHTML = `<a href="">${el.innerHTML}</a>`;
            el.firstElementChild.href = productUrl;
          });
          newDetail.querySelectorAll('.show-gallery').forEach((el) => {
            el.classList.remove('show-gallery');
            el.href = productUrl;
          });
          newDetail.querySelectorAll('.not-in-quickbuy').forEach((el) => { el.remove(); });
          newDetail.querySelectorAll('.only-in-quickbuy').forEach((el) => { el.classList.remove('only-in-quickbuy'); });
          newDetail.querySelectorAll('.sticky-content-container').forEach((el) => { el.classList.remove('sticky-content-container'); });
          newDetail.querySelectorAll('pickup-availability').forEach((el) => { el.remove(); });
          newDetail.querySelectorAll('variant-picker[data-update-url="true"]').forEach((el) => { el.dataset.updateUrl = false; });
          newDetail.querySelectorAll('noscript').forEach((el) => el.remove());

          // enforce thumbnails-under gallery layout
          newDetail.querySelectorAll('.media-gallery .main-image carousel-slider').forEach((el) => { el.classList.remove('mobile-only'); });
          newDetail.querySelectorAll('.product-media-collage').forEach((el) => { el.remove(); });
          newDetail.querySelectorAll('.media-gallery .thumbnails').forEach((el) => { el.classList.remove('mobile-only'); });
          ['media-gallery--layout-carousel-beside', 'media-gallery--layout-columns-1', 'media-gallery--layout-columns-2', 'media-gallery--layout-collage-1', 'media-gallery--layout-collage-2'].forEach((cl) => {
            newDetail.querySelectorAll(`.${cl}`).forEach((el) => {
              el.classList.remove(cl);
              el.classList.add('media-gallery--layout-carousel-under');
            });
          });

          // add any external resources
          tmpl.content.querySelectorAll('.section-main-product link[rel="stylesheet"]').forEach((el) => {
            if (!document.querySelector(`link[rel="stylesheet"][href="${el.getAttribute('href')}"]`)) {
              document.head.insertAdjacentHTML('beforeend', el.outerHTML);
            }
          });
          tmpl.content.querySelectorAll('.section-main-product script[src]').forEach((el) => {
            if (!document.querySelector(`script[src="${el.getAttribute('src')}"]`)) {
              const scr = document.createElement('script');
              scr.src = el.getAttribute('src');
              scr.defer = 'defer';
              document.head.appendChild(scr);
            }
          });

          drawerContent.innerHTML = '';
          drawerContent.appendChild(newDetail);
          
          
        } catch (error) {
          console.error('Error loading product form:', error);
          drawerContent.innerHTML = '<div class="error">Error loading product. Please try again.</div>';
        }
    }, true);
    
    // Close drawer when clicking the close button
    drawer.addEventListener('on:cart:add', closeDrawer);
closeButton.addEventListener('click', closeDrawer);
    
    // Close drawer when clicking backdrop
    backdrop.addEventListener('click', closeDrawer);
  });

/* Vlinks accessibility integration - 2026-10-17.
 * Repair app markup after asynchronous rendering, without changing appearance.
 * Keep real buttons and consent text available to screen readers and keyboards.
 */
(() => {
  'use strict';
  let pending = false;
  const he = () => document.documentElement.lang.toLowerCase().startsWith('he');
  const set = (el, name, value) => {
    if (el.getAttribute(name) !== value) el.setAttribute(name, value);
  };
  const text = (el) => (el?.textContent || '').replace(/\s+/g, ' ').trim();
  const hasLabel = (el) => {
    const ids = (el.getAttribute('aria-labelledby') || '').split(/\s+/).filter(Boolean);
    return ids.some((id) => text(document.getElementById(id)));
  };
  function repair() {
    pending = false;
    const toolbar = document.getElementById('enable-toolbar-buttons');
    if (toolbar) {
      toolbar.removeAttribute('aria-labeledby');
      set(toolbar, 'aria-labelledby', 'enable-toolbar-trigger');
      // Vlinks 2026-10-20: toolbar wrappers are presentational, including unlabelled li.
      if (toolbar.getAttribute('role') === 'toolbar') {
        toolbar.querySelectorAll(':scope > li').forEach((item) => {
          if (item.querySelector(':scope > button')) set(item, 'role', 'none');
        });
      }
    }
    const enableDialog = document.getElementById('enable-modal');
    if (enableDialog && !hasLabel(enableDialog)) {
      set(enableDialog, 'aria-label', he() ? 'הגדרות נגישות' : 'Accessibility settings');
    }
    document.querySelectorAll('#enable-toolbar button[data-enable-lang]').forEach((button) => {
      const name = button.getAttribute('data-enable-lang');
      set(button, 'aria-label', name === 'he_IL' ? 'עברית' : name === 'en_US' ? 'English' : name);
    });
    document.querySelectorAll('.yotpo-widget-spotlight-cta[aria-role]').forEach((cta) => {
      const role = cta.getAttribute('aria-role');
      if (role === 'button') {
        set(cta, 'role', 'button');
        set(cta, 'tabindex', '0');
      }
      cta.removeAttribute('aria-role');
    });
    document.querySelectorAll('.wishlist-engine[data-product_id]').forEach((button) => {
      set(button, 'role', 'button');
      set(button, 'tabindex', '0');
      const added = button.getAttribute('data-added') === 'true';
      set(button, 'aria-pressed', String(added));
      const card = button.closest('product-block');
      const title = text(card?.querySelector('.product-block__title, .product-title'));
      const action = he() ? (added ? 'הסרה מרשימת המשאלות' : 'הוספה לרשימת המשאלות') :
        (added ? 'Remove from wishlist' : 'Add to wishlist');
      set(button, 'aria-label', action + (title ? ': ' + title : ''));
    });
    // Vlinks 2026-10-17: names for Tellos desktop carousel arrows.
    document.querySelectorAll('#tellos-carousel button.button-prev, #tellos-carousel button.button-next').forEach((button) => {
      const previous = button.classList.contains('button-prev');
      set(button, 'aria-label', he() ? (previous ? 'הסרטונים הקודמים' : 'הסרטונים הבאים') :
        (previous ? 'Previous videos' : 'Next videos'));
    });
    // Flashy accessibility is configured in its native popup editor.

  }
  function schedule() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(repair);
  }
  function start() {
    repair();
    new MutationObserver((records) => {
      if (records.some((record) => record.type === 'childList' ||
        (record.target instanceof Element && record.target.matches('.wishlist-engine, .yotpo-widget-spotlight-cta')))) schedule();
    }).observe(document.body, {childList: true, subtree: true, attributes: true,
      attributeFilter: ['data-added', 'aria-role']});
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const target = event.target;
      if (!(target instanceof Element) || !target.matches('.wishlist-engine[role="button"], .yotpo-widget-spotlight-cta[role="button"]')) return;
      if (event.repeat) return;
      event.preventDefault();
      target.click();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();


/* Vlinks 2026-10-17: consent labels in Flashy's isolated popup markup.
 * Preserve checkbox IDs, required validation, consent copy and visual styles.
 */
(() => {
  'use strict';
  const watchedRoots = new WeakSet();
  function connect(host) {
    const root = host.shadowRoot;
    if (!root || watchedRoots.has(root)) return;
    watchedRoots.add(root);
    let pending = false;
    function repair() {
      pending = false;
      root.querySelectorAll('form.fls-popup-form[role="button"]').forEach((form) => {
        form.removeAttribute('role');
      });
      root.querySelectorAll('.fls-popup-form input[type="checkbox"]').forEach((input) => {
        const group = input.closest('.form-group');
        const label = group?.querySelector('label');
        const copy = (label?.textContent || '').replace(/\s+/g, ' ').trim();
        if (copy && input.getAttribute('aria-label') !== copy) input.setAttribute('aria-label', copy);
      });
    }
    repair();
    new MutationObserver(() => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(repair);
    }).observe(root, {childList: true, subtree: true, attributes: true, attributeFilter: ['role']});
  }
  function scan(node) {
    if (!(node instanceof Element)) return;
    if (node.matches('flashy-popup')) {
      connect(node);
      if (!node.shadowRoot) setTimeout(() => connect(node), 500);
    }
    node.querySelectorAll('flashy-popup').forEach(connect);
  }
  function start() {
    scan(document.documentElement);
    new MutationObserver((records) => records.forEach((record) => {
      record.addedNodes.forEach(scan);
    })).observe(document.documentElement, {childList: true, subtree: true});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();
